import {
  AccountTreeOutlined,
  BookmarkAdd,
  CloudUploadOutlined,
  GradingOutlined,
  LocalPolice,
  NotificationsOutlined,
  OpenInNew,
  PeopleOutlined,
  Person,
  Place,
} from '@mui/icons-material';
import { Hidden, IconButton } from '@mui/material';
import { Loader } from 'components/common/Loader';
import { userRoles } from 'constants/constants';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { TaskList } from 'pages/Tasks/TaskList';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAllTasksMutation,
  useGetNotificationsMutation,
  useMarkAllNotificationAsReadMutation,
} from 'redux/services/project';
import { WorkFlowButton } from './WorkflowButton';
import './ProjectDashboard.scss';
import { NotificationList } from 'components/Notifications/NotificationList';
import { useDispatch } from 'react-redux';
import { setNotifications } from 'redux/projectSlice';

const ProjectDashboard = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { isLoading, project } = useSetDropdownAndRole(false);
  const navigate = useNavigate();
  const [getAllTasks, { data: allTasks, isLoading: allTasksLoading }] =
    useGetAllTasksMutation();
  const [
    getNotifications,
    { data: notifications, isLoading: notificationsLoading },
  ] = useGetNotificationsMutation();

  const [markAllAsRead, { isLoading: markAsReadNotificationsApiLoading }] =
    useMarkAllNotificationAsReadMutation();

  const fetchNotifications = useCallback(async () => {
    await markAllAsRead({});
    getNotifications({ projectId, notificationType: 'project' });
  }, [projectId, markAllAsRead, getNotifications]);

  useEffect(() => {
    getAllTasks({ projectId });
    fetchNotifications();
  }, [getAllTasks, fetchNotifications, projectId]);

  useEffect(() => {
    if (notifications?.data?.length) {
      dispatch(setNotifications(notifications));
    }
  }, [notifications, dispatch]);

  const handleOpenTask = () => {
    navigate(`/project/${projectId}/tasks`);
  };

  const getWorkflowClickHandler = (path: string) => () => {
    navigate(path);
  };

  const renderMoreDetailsPills = () => (
    <div className="d-flex more-details-container gap-2 flex-wrap">
      <div className="pill location">
        <Place fontSize="small" color="inherit" />
        <span className="mx-1">{project?.projectInfo.location}</span>
      </div>
      {project?.role !== userRoles.OWNER ? (
        <>
          <div className="pill role">
            <Person fontSize="small" color="inherit" />
            <span className="mx-1">
              {t('role-label', { role: project?.role ?? '' })}
            </span>
          </div>
          <div className="pill owner">
            <LocalPolice fontSize="small" color="inherit" />
            <span className="mx-1">
              {t('role-owner', {
                owner: project?.team?.find((t) => t.role === userRoles.OWNER)
                  ?.name,
              })}
            </span>
          </div>
        </>
      ) : (
        <div className="pill owner">
          <LocalPolice fontSize="small" color="inherit" />
          <span className="mx-1">
            {t('role-label', { role: project?.role ?? '' })}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="project-dashboard overflow-auto">
      {!isLoading &&
      !allTasksLoading &&
      !notificationsLoading &&
      !markAsReadNotificationsApiLoading ? (
        <div className="d-flex w-100 h-100 flex-column gap-3">
          <div className="project-and-links-container d-flex flex-column flex-md-row gap-3">
            <div className="d-flex flex-column flex-grow-1">
              <div className="project-details-container d-flex w-100">
                <span className="project-img">
                  <img
                    alt={project?.projectInfo.projectName}
                    src={
                      project?.projectInfo.projectImg ||
                      `https://ui-avatars.com/api/?size=80&color=fff&rounded=true&bold=true&background=random&name=${project?.projectInfo.projectName}`
                    }
                  />
                </span>
                <div className="d-flex flex-column flex-grow-1">
                  <div className="project-title-container d-flex flex-column align-items-end">
                    <span className="h3">
                      {project?.projectInfo.projectName}
                    </span>
                    <span className="h4">{project?.projectInfo.projectId}</span>
                  </div>
                  <Hidden mdDown>{renderMoreDetailsPills()}</Hidden>
                </div>
              </div>
              <Hidden mdUp>
                <div className="mobile-more-details-container">
                  {renderMoreDetailsPills()}
                </div>
              </Hidden>
              <div className="mt-2 d-flex flex-grow-1 gap-2 gap-md-3 flex-wrap justify-content-center justify-content-md-start align-items-center align-content-start">
                <WorkFlowButton
                  count={
                    project?.inspectionsCount
                      ? project.inspectionsCount.toLocaleString('en-US', {
                          minimumIntegerDigits: 2,
                          useGrouping: false,
                        })
                      : '0'
                  }
                  title="Inspection"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/inspection`,
                  )}
                />
                <WorkFlowButton
                  count={
                    project?.observationsCount
                      ? project.observationsCount.toLocaleString('en-US', {
                          minimumIntegerDigits: 2,
                          useGrouping: false,
                        })
                      : '0'
                  }
                  title="Observation"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/observation`,
                  )}
                />
                <WorkFlowButton
                  count={
                    project?.incidentsCount
                      ? project.incidentsCount.toLocaleString('en-US', {
                          minimumIntegerDigits: 2,
                          useGrouping: false,
                        })
                      : '0'
                  }
                  title="Incident"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/incident`,
                  )}
                />
              </div>
            </div>
            <div className="quicklinks-container">
              <div className="list-header d-flex align-items-center justify-content-between px-2">
                <IconButton color="inherit">
                  <BookmarkAdd color="inherit" />
                </IconButton>
                <span className="h5 align-self-center mb-0">
                  {t('label_quick_links')}
                </span>
                <IconButton />
              </div>
              <div className="d-flex flex-column p-2">
                <div
                  className="mb-2 quick-link-item"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/edit-sub-projects`,
                  )}
                >
                  <AccountTreeOutlined />
                  <span className="mx-2">{t('label_sub_projects')}</span>
                </div>
                <div
                  className="mb-2 quick-link-item"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/edit-checklist`,
                  )}
                >
                  <GradingOutlined />
                  <span className="mx-2">{t('checklist')}</span>
                </div>
                <div
                  className="mb-2 quick-link-item"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/edit-documents`,
                  )}
                >
                  <CloudUploadOutlined />
                  <span className="mx-2">{t('label_documents')}</span>
                </div>
                <div
                  className="quick-link-item"
                  onClick={getWorkflowClickHandler(
                    `/project/${projectId}/edit-teams`,
                  )}
                >
                  <PeopleOutlined />
                  <span className="mx-2">{t('header_users')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="task-and-notification-container d-flex flex-column flex-md-row gap-2">
            <div className="list-container d-flex flex-column">
              <div className="list-header d-flex align-items-center justify-content-between px-2">
                <IconButton
                  title="More"
                  color="inherit"
                  onClick={handleOpenTask}
                >
                  <OpenInNew color="inherit" />
                </IconButton>
                <span className="h5 align-self-center mb-0">
                  {t('label_tasks')}
                </span>
                <IconButton></IconButton>
              </div>
              <div className="p-2 flex-grow-1 overflow-auto">
                <TaskList tasks={allTasks?.data ?? []} />
              </div>
            </div>
            <div className="list-container d-flex flex-column">
              <div className="list-header d-flex align-items-center justify-content-between px-2">
                <IconButton title="More" sx={{ color: '#fff' }}>
                  <NotificationsOutlined color="inherit" />
                </IconButton>
                <span className="h5 align-self-center mb-0">
                  {t('label_notifications_updates')}
                </span>
                <IconButton></IconButton>
              </div>
              <div className="p-2 flex-grow-1 overflow-auto w-100">
                <NotificationList notifications={notifications?.data ?? []} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex p-3 w-100 flex-grow-1 justify-content-center">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard;
